import {
  API_ORIGIN,
  authorizedFetch,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Textarea,
} from '@yeshuman/sdk'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

type Recipient = {
  id: string
  email: string
  full_name: string
  is_self: boolean
}

type PreviewBody = {
  html: string
  recipient_count: number
}

type Preview = {
  full?: PreviewBody
  redacted?: PreviewBody
}

async function readError(response: Response, fallback: string) {
  const body = await response.json().catch(() => ({}))
  return body.detail || body.error || fallback
}

export function ReportEmailDialog({
  open,
  onOpenChange,
  kind,
  subjectId,
  asRecruiter,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  kind: 'job' | 'brief'
  subjectId: string
  asRecruiter: boolean
}) {
  const [recipients, setRecipients] = useState<Recipient[]>([])
  const [selected, setSelected] = useState<string[]>([])
  const [note, setNote] = useState('')
  const [preview, setPreview] = useState<Preview>({})
  const [view, setView] = useState<'full' | 'redacted'>('full')
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)

  useEffect(() => {
    if (!open) return
    setNote('')
    setPreview({})
    setView('full')
    let cancelled = false
    void (async () => {
      const params = new URLSearchParams({ kind, subject_id: subjectId })
      const response = await authorizedFetch(`${API_ORIGIN}/api/applications/reports/recipients/?${params}`)
      if (!response.ok || cancelled) return
      const rows = (await response.json()) as Recipient[]
      if (cancelled) return
      setRecipients(rows)
      setSelected(rows.filter((row) => row.is_self).map((row) => row.id))
    })()
    return () => {
      cancelled = true
    }
  }, [open, kind, subjectId])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    const handle = window.setTimeout(() => {
      void (async () => {
        setLoading(true)
        const response = await authorizedFetch(`${API_ORIGIN}/api/applications/reports/preview/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            kind,
            subject_id: subjectId,
            note,
            recipient_ids: selected,
            as_recruiter: asRecruiter,
          }),
        })
        setLoading(false)
        if (!response.ok || cancelled) return
        const body = (await response.json()) as Preview
        if (cancelled) return
        setPreview(body)
        setView((current) => {
          if (body[current]) return current
          if (body.redacted && !body.full) return 'redacted'
          return 'full'
        })
      })()
    }, 250)
    return () => {
      cancelled = true
      window.clearTimeout(handle)
    }
  }, [open, kind, subjectId, note, selected, asRecruiter])

  const shown = preview[view]
  const fullCount = preview.full?.recipient_count ?? 0
  const redactedCount = preview.redacted?.recipient_count ?? 0
  const sendLabel = useMemo(() => {
    if (fullCount && redactedCount) {
      return `Send full to ${fullCount}, redacted to ${redactedCount}`
    }
    if (redactedCount) return `Send redacted report to ${redactedCount}`
    if (fullCount) return `Send report to ${fullCount}`
    return 'Send report'
  }, [fullCount, redactedCount])

  function toggle(id: string) {
    setSelected((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]))
  }

  async function send() {
    setSending(true)
    const response = await authorizedFetch(`${API_ORIGIN}/api/applications/reports/send/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        kind,
        subject_id: subjectId,
        note,
        recipient_ids: selected,
        as_recruiter: asRecruiter,
      }),
    })
    setSending(false)
    if (!response.ok) {
      toast.error(await readError(response, 'Could not send the report'))
      return
    }
    toast.success('Report sent')
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Email report</DialogTitle>
          <DialogDescription>
            This email is a short cover. The full analysis opens from a link that lasts seven days. A recruiter copy to the employer replaces names and contact details.
          </DialogDescription>
        </DialogHeader>
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">Recipients</legend>
          {recipients.map((recipient) => (
            <label key={recipient.id} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={selected.includes(recipient.id)}
                onChange={() => toggle(recipient.id)}
              />
              <span>
                {recipient.full_name}
                {recipient.is_self ? ' (you)' : ''}
                <span className="text-muted-foreground"> · {recipient.email}</span>
              </span>
            </label>
          ))}
        </fieldset>
        <label className="mt-4 block space-y-2 text-sm font-medium">
          Note
          <Textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Optional message at the top of the email"
            rows={3}
          />
        </label>
        {preview.full && preview.redacted ? (
          <div className="mt-4 flex gap-2">
            <Button type="button" size="sm" variant={view === 'full' ? 'default' : 'outline'} onClick={() => setView('full')}>
              Your copy
            </Button>
            <Button
              type="button"
              size="sm"
              variant={view === 'redacted' ? 'default' : 'outline'}
              onClick={() => setView('redacted')}
            >
              Employer copy
            </Button>
          </div>
        ) : null}
        <div className="mt-4 overflow-hidden rounded-md border border-border bg-muted/30">
          {shown?.html ? (
            <iframe title="Report preview" className="h-80 w-full bg-white" srcDoc={shown.html} />
          ) : (
            <p className="p-4 text-sm text-muted-foreground">{loading ? 'Preparing preview…' : 'Nothing to preview yet.'}</p>
          )}
        </div>
        <DialogFooter className="mt-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" disabled={sending || selected.length === 0} onClick={() => void send()}>
            {sendLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
